import * as __vanilla_filescope__ from '@vanilla-extract/css/fileScope';
__vanilla_filescope__.setFileScope("src/styles.css.ts", "@paypalcorp/pp-react-sheet");
import { globalStyle, style } from '@vanilla-extract/css';
import { tokens, breakpoints } from '@paypalcorp/pp-react-theme';
import { utilStyles, ZINDEX_MODAL } from '@paypalcorp/pp-react-utils';
const {
  variables
} = utilStyles;
const {
  ppuiEaseInOut
} = variables;
const dialogWidth = '75%'; // 8 columns in a 12 column grid
const dialogMaxWidth = `calc(${tokens.size.media.xl} * 0.75)`; // 75% of XL screen grid max width

const minHeight = '3rem';
const maxHeight = '90dvh';
const paddingLgMedia = '8.5rem';
const paddingMdMedia = '5rem';
const headerHeightMobile = `calc(${tokens.size.icon.lg} + (${tokens.space['100']}))`;
const headerHeightDesktop = `calc(${tokens.size.icon.xl} + (${tokens.space['100']}))`;
const widthSm = '100vw';
const drawerWidth = '35.5rem';
const MEDIA_QUERY_LG_MAX = `screen and (max-width: ${breakpoints.mediaLg})`;
const MEDIA_QUERY_MD_MAX = `screen and (max-width: ${breakpoints.mediaMd})`;
const MEDIA_QUERY_SM_MAX = `screen and (max-width: ${breakpoints.mediaSm})`;
const MEDIA_QUERY_MD_MIN = `screen and (min-width: ${breakpoints.mediaMd})`;
const verticalMoveNone = {
  transform: 'translateZ(0) translateY(0)'
};
const verticalMoveFull = {
  transform: 'translateZ(0) translateY(100%)'
};
const lateralMoveNone = {
  transform: 'translateZ(0) translateX(0)'
};
const lateralMoveFull = {
  transform: 'translateZ(0) translateX(100%)'
};
const lateralMoveFullNegative = {
  transform: 'translateZ(0) translateX(-100%)'
};
const borderTopRadii = {
  borderTopLeftRadius: tokens.border.radius.jumbo,
  borderTopRightRadius: tokens.border.radius.jumbo
};
const containerCenter = (alignment = 'center') => {
  return style({
    display: 'flex',
    alignItems: alignment,
    justifyContent: 'space-around'
  }, `sheet-container_center_${alignment}`);
};
const getHeightStylesByPercent = percent => {
  return style({
    '@media': {
      [MEDIA_QUERY_MD_MAX]: {
        height: `${percent}dvh`,
        ...borderTopRadii
      }
    }
  }, `sheet_height_${percent}`);
};
const getContainerSideStyles = side => {
  const sideStyles = side === 'right' ? lateralMoveFull : lateralMoveFullNegative;
  return style([commonContainerStyles, {
    '@media': {
      [MEDIA_QUERY_MD_MIN]: sideStyles
    },
    selectors: {
      [`&[aria-hidden='false']`]: lateralMoveNone
    }
  }], `sheet-container_${side}`);
};
const containerBottom = style([containerCenter('flex-end'), {
  width: tokens.size.media.md,
  '@media': {
    [MEDIA_QUERY_LG_MAX]: {
      width: tokens.size.media.sm
    },
    [MEDIA_QUERY_SM_MAX]: {
      width: widthSm
    }
  }
}], 'sheet-container_bottom');
const sheetNoContainer = style({
  width: dialogWidth,
  maxWidth: dialogMaxWidth
}, 'sheet_no_container');
const sheet = style({
  height: '100dvh',
  width: 'auto',
  willChange: 'transform',
  transition: `transform ${tokens.motion.duration.depart} ${ppuiEaseInOut},
      height ${tokens.motion.duration.depart} ${ppuiEaseInOut}`,
  ...verticalMoveFull,
  '@media': {
    [MEDIA_QUERY_MD_MAX]: {
      width: widthSm,
      height: 'auto',
      minHeight: minHeight,
      maxHeight: maxHeight,
      ...borderTopRadii
    }
  }
}, 'sheet');
const sheetShow = style(verticalMoveNone, 'sheet_show');
const sheetBackdrop = style([containerCenter('center'), {
  position: 'fixed',
  inset: 0,
  zIndex: ZINDEX_MODAL,
  visibility: 'hidden',
  opacity: 0,
  willChange: 'opacity',
  transition: `opacity ${tokens.motion.duration.depart} ease-in-out,
    visibility ${tokens.motion.duration.depart} ${tokens.motion.duration.depart}`,
  backgroundColor: tokens.color.overlay.mainHover,
  boxShadow: 'none'
}], 'sheet_backdrop');
globalStyle(`${sheetBackdrop} [aria-hidden="false"], ${sheetBackdrop} [aria-hidden="true"]`, {
  transitionDuration: tokens.motion.duration.arrive
});
const scrim = style([{
  position: 'fixed',
  inset: 0,
  zIndex: ZINDEX_MODAL,
  transform: `visibiliy ${tokens.motion.duration.depart} ${tokens.motion.duration.depart}`,
  selectors: {
    '&::before': {
      content: ' ',
      position: 'absolute',
      inset: 0,
      opacity: 0,
      willChange: 'opacity',
      transition: `opacity ${tokens.motion.duration.depart} ease-in-out`,
      backgroundColor: tokens.color.overlay.mainHover
    }
  }
}, containerCenter('flex-end')], 'sheet-scrim');
const scrimShow = style({
  visibility: 'visible',
  transition: 'none' /* visibility should not be animated when coming in */,
  selectors: {
    '&::before': {
      opacity: 1,
      transitionDuration: tokens.motion.duration.arrive
    }
  }
}, 'sheet-scrim_show');
const showGripper = style({
  selectors: {
    '&::before': {
      content: ' ',
      position: 'absolute',
      top: tokens.space['100'],
      left: 0,
      right: 0,
      margin: 'auto',
      width: tokens.size.tapTarget.default,
      height: tokens.size.indicator.sm,
      backgroundColor: tokens.color.background.mediumContrast,
      borderRadius: tokens.border.radius.md,
      display: 'none',
      '@media': {
        [MEDIA_QUERY_MD_MAX]: {
          display: 'block'
        }
      }
    }
  }
}, 'sheet-show_gripper');
const sheetContentWithHeader = style({
  '@media': {
    [MEDIA_QUERY_MD_MAX]: {
      borderRadius: 0
    }
  }
}, 'sheet-content_with_header');
const sheetContent = style({
  padding: `0 ${paddingLgMedia}`,
  '@media': {
    [MEDIA_QUERY_LG_MAX]: {
      padding: `0 ${paddingMdMedia}`
    },
    [MEDIA_QUERY_MD_MAX]: {
      padding: `0 ${tokens.space['200']} ${tokens.space['200']}`
    }
  }
}, 'sheet-content');
const sheetHeader = style({
  minHeight: headerHeightDesktop,
  '@media': {
    [MEDIA_QUERY_MD_MAX]: {
      minHeight: headerHeightMobile,
      borderRadius: 0
    }
  }
}, 'sheet-header');
const sheetHeaderAction = style({
  minHeight: headerHeightDesktop,
  padding: tokens.space['150'],
  margin: 0,
  '@media': {
    [MEDIA_QUERY_MD_MAX]: {
      minHeight: headerHeightMobile,
      padding: tokens.space['100'],
      margin: 0,
      borderRadius: 0
    }
  }
}, 'sheet-header_action');
const sheetHeaderWorkArea = style({
  flex: '0 0 100%',
  padding: `${tokens.space['100']} ${tokens.space['200']}`
}, 'sheet-header_work_area');
const sheetContentsClose = style({
  position: 'absolute',
  right: tokens.space['50'],
  top: tokens.space['50'],
  zIndex: 2,
  selectors: {
    '[dir="rtl"] &': {
      left: tokens.space['50'],
      right: 'auto',
      '@media': {
        [MEDIA_QUERY_MD_MIN]: {
          left: tokens.size.indicator.md
        }
      }
    }
  }
}, 'sheet-content_close');
globalStyle(`${sheetContentsClose}`, {
  '@media': {
    [MEDIA_QUERY_MD_MIN]: {
      right: tokens.size.indicator.md,
      top: tokens.space['150']
    }
  }
});
const sheetHeight100 = style({
  '@media': {
    [MEDIA_QUERY_MD_MAX]: {
      height: '100dvh',
      maxHeight: '100dvh',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0
    }
  }
}, 'sheet_height_100');
const sheetHeight90 = getHeightStylesByPercent('90');
const sheetHeight50 = getHeightStylesByPercent('50');
const justifyRight = style({
  '@media': {
    [MEDIA_QUERY_MD_MIN]: {
      justifyContent: 'flex-end'
    }
  }
}, 'sheet_right_side');
const containerRightWidth = style({
  '@media': {
    [MEDIA_QUERY_MD_MIN]: {
      width: drawerWidth
    }
  }
}, 'sheet-right_width');
const commonContainerStyles = style({
  '@media': {
    [MEDIA_QUERY_MD_MIN]: {
      willChange: 'transform',
      transition: `transform ${tokens.motion.duration.depart}`,
      bottom: 'inherit'
    }
  }
}, 'sheet-container');
const containerRight = getContainerSideStyles('right');
const containerLeft = getContainerSideStyles('left');
const drawerContainer = style({
  '@media': {
    [MEDIA_QUERY_MD_MIN]: {
      paddingLeft: tokens.space['600'],
      paddingRight: tokens.space['600']
    }
  }
}, 'sheet-drawer_container');
export { sheet, sheetShow, sheetHeight100, sheetHeight90, sheetHeight50, sheetNoContainer, sheetHeader, sheetHeaderAction, sheetHeaderWorkArea, sheetContent, sheetContentWithHeader, scrim, scrimShow, showGripper, containerBottom, justifyRight, containerRightWidth, containerRight, containerLeft, drawerContainer };
__vanilla_filescope__.endFileScope();
